import React, { useState } from 'react';
import Button3D from '../../ui/Button3D';
import TimeCombo from '../foundation/TimeCombo';
import '../foundation/eventsFoundation.css';
import { settingsProblem, settingsSubtitle } from '../eventSettings';
import { WORD_MAX, resolveEventWords } from '../eventWords';
import SettingsPopup, { Field } from './SettingsPopup';

// Module 19 (BL-380) G8 - "General": what a new event starts with. Blank means no default (today's behavior).
export default function GeneralSettingsPopup({ settings, kits, saving, error, onSave, onClose }) {
    const [form, setForm] = useState({
        default_start_time: settings.default_start_time,
        default_end_time: settings.default_end_time,
        default_event_type: settings.default_event_type,
        default_expected_guest_count: settings.default_expected_guest_count,
        event_word: settings.event_word,
        crew_word: settings.crew_word,
    });
    const [problem, setProblem] = useState('');
    const set = (name) => (event) => setForm((previous) => ({ ...previous, [name]: event.target.value }));
    const activeKits = kits.filter((kit) => kit.is_active);
    // a saved default that was turned off still shows, so it can be seen and changed
    const saved = kits.find((kit) => kit.key === form.default_event_type);

    const submit = (event) => {
        event?.preventDefault?.();
        const next = { ...settings, ...form };
        const message = settingsProblem(next);
        if (message) return setProblem(message);
        setProblem('');
        onSave(next);
    };
    const shown = problem || error;
    // a live preview of the words as the screens will show them
    const saved_words = resolveEventWords(settings);
    const preview = resolveEventWords({ event_word: form.event_word, crew_word: form.crew_word });

    return (
        <SettingsPopup
            title="General"
            subtitle={settingsSubtitle(saved_words)}
            tileKey="general"
            testId="events-settings-general"
            onClose={onClose}
            actions={(
                <>
                    <Button3D onClick={onClose} disabled={saving}>Cancel</Button3D>
                    <Button3D variant="go" onClick={submit} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button3D>
                </>
            )}
        >
            <form onSubmit={submit} noValidate>
                <ul className="ia-resv-grid" aria-label="General defaults">
                    <Field id="set-start" label="Default start time"><TimeCombo id="set-start" label="Default start time" value={form.default_start_time} onChange={(default_start_time) => setForm((previous) => ({ ...previous, default_start_time }))} /></Field>
                    <Field id="set-end" label="Default end time"><TimeCombo id="set-end" label="Default end time" value={form.default_end_time} onChange={(default_end_time) => setForm((previous) => ({ ...previous, default_end_time }))} /></Field>
                    <Field id="set-type" label={`Default ${saved_words.eventLower} type`} wide>
                        <select id="set-type" className="mt-[3px] w-full min-h-[34px] rounded-lg border border-[#64748B] bg-[#0B1220] px-2 text-white" value={form.default_event_type} onChange={set('default_event_type')}>
                            <option value="">No default</option>
                            {activeKits.map((kit) => <option key={kit.key} value={kit.key}>{kit.label}</option>)}
                            {saved && !saved.is_active && <option value={saved.key}>{saved.label} (turned off)</option>}
                        </select>
                    </Field>
                    <Field id="set-guests" label="Default guests expected" wide hint="Leave blank for no default.">
                        <input id="set-guests" type="number" min="0" inputMode="numeric" value={form.default_expected_guest_count} onChange={set('default_expected_guest_count')} />
                    </Field>
                </ul>
                <p className="mt-3 text-sm">These fill in a new {saved_words.eventLower}. On any one {saved_words.eventLower} you can change them, and that never changes this setting.</p>

                <h3 className="mb-2 mt-4 text-sm font-bold uppercase tracking-wide">Words your clients see</h3>
                <ul className="ia-resv-grid" aria-label="Words your clients see">
                    <Field id="set-event-word" label="Call an event a…" hint={`For example Party, Celebration or Show. Shown as “${preview.event}” and “${preview.events}”.`}>
                        <input id="set-event-word" type="text" maxLength={WORD_MAX} autoComplete="off" value={form.event_word} onChange={set('event_word')} data-testid="set-event-word" />
                    </Field>
                    <Field id="set-crew-word" label="Call a crew member a…" hint={`For example Staff, Team or Squad. Shown as “${preview.crew}” and “${preview.crews}”.`}>
                        <input id="set-crew-word" type="text" maxLength={WORD_MAX} autoComplete="off" value={form.crew_word} onChange={set('crew_word')} data-testid="set-crew-word" />
                    </Field>
                </ul>
                <p className="mt-2 text-sm">Used on the Events screens and anything your clients and guests see. The defaults are Event and Crew. Letters, numbers, spaces, &amp;, - and &apos; only, up to {WORD_MAX}.</p>
                {shown && <p className="ia-resv-error" role="alert">{shown}</p>}
            </form>
        </SettingsPopup>
    );
}
